// Home Stock category badge with PressableHint tooltip (tap + hover), same pattern as HomeStockLevelIcon.

import { useId } from 'react';
import { inventoryV2CategoryBadgeClassName } from '../../lib/inventoryCategoryBadge';
import { PressableHint } from '../ui/PressableHint';

type InventoryCategoryBadgeVariant = 'detail' | 'inline';

type InventoryCategoryBadgeProps = {
  category: string;
  /** Display label when different from `category` (e.g. translated text). */
  displayCategory?: string;
  badgeIndexMap?: ReadonlyMap<string, number>;
  variant?: InventoryCategoryBadgeVariant;
  hintId?: string;
};

const VARIANT_TRIGGER_CLASS: Record<InventoryCategoryBadgeVariant, string> = {
  detail:
    'max-w-[min(78vw,100%)] rounded-tr-2xl rounded-bl-xl border border-r-0 border-t-0 px-2.5 py-1 !text-right text-[12px] font-bold leading-tight max-md:whitespace-normal max-md:break-words md:max-w-[min(148px,46vw)] md:truncate',
  inline: 'max-w-[min(140px,85vw)] truncate rounded-lg border px-2.5 py-1 text-xs font-bold',
};

const DETAIL_ROOT_CLASS = '!absolute right-0 top-0 z-[1] leading-none';

export const InventoryCategoryBadge = ({
  category,
  displayCategory,
  badgeIndexMap,
  variant = 'inline',
  hintId: hintIdProp,
}: InventoryCategoryBadgeProps) => {
  const trimmed = category.trim();
  const label = (displayCategory ?? category).trim();
  const autoHintId = useId();
  const hintId = hintIdProp ?? `cat-badge-${autoHintId}`;

  if (trimmed === '') {
    return null;
  }

  const badgeClassName = inventoryV2CategoryBadgeClassName(trimmed, badgeIndexMap);
  const triggerClassName = `${VARIANT_TRIGGER_CLASS[variant]} ${badgeClassName}`.trim();

  return (
    <PressableHint
      hintId={hintId}
      detail={label}
      ariaLabel={label}
      placement="top"
      rootClassName={variant === 'detail' ? DETAIL_ROOT_CLASS : undefined}
      triggerClassName={triggerClassName}
    >
      {label}
    </PressableHint>
  );
};
